import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Upload, Settings as SettingsLR, User, Activity, Lock, Database, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { loggingService, ActivityLog } from '../services/loggingService';
import { authAPI } from '../services/api';
import api from '../services/api';

const Settings: React.FC = () => {
    const {user, logout} = useAuth();
    const { isDark } = useDarkMode();
    const navigate = useNavigate();
    const hasLoggedPageView = useRef(false);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'general' | 'password' | 'data' | 'logs'>('general');
    const [logs, setLogs] = useState<ActivityLog[]>([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);

    const handleExport = async() => {
        setLoading(true);
        try{
            const response = await api.get('/auth/export');
            
            // Download JSON file
            const blob = new Blob([JSON.stringify(response.data.data, null, 2)], {type: 'application/json'});
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Format date as yyyy-mm-dd-hh-mm-ss
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}-${hours}-${minutes}-${seconds}`;
            
            link.download = `manager-datas-${user?.id || 'unknown'}-${dateStr}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            // Save export log
            await loggingService.logExport();

            showAlert('success', 'Data exported successfully');
        }catch(error){
            console.error('Export error:', error);
            showAlert('error', 'Data export failed');
        }finally{
            setLoading(false);
        }
    };

    const handleImport = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = async(event: any) => {
            const file = event.target.files[0];
            if(!file){
                return;
            }

            try{
                const text = await file.text();
                const importData = JSON.parse(text);

                // Check JSON file format
                if(!importData.api_keys && !importData.passwords && !importData.tags){
                    showAlert('error', 'Invalid file format. Please use an exported JSON file.');
                    return;
                }

                setLoading(true);

                // Send import request to API
                const response = await api.post('/auth/import', {
                    data: {
                        api_keys: importData.api_keys || [],
                        passwords: importData.passwords || [],
                        tags: importData.tags || []
                    }
                });

                showAlert('success', `Import successful! ${response.data.data.imported_api_keys} API keys, ${response.data.data.imported_passwords} passwords, ${response.data.data.imported_tags} tags imported.`);
                
                // Save import log
                await loggingService.logImport(response.data.data.imported_api_keys + response.data.data.imported_passwords);
                
                setTimeout(() => {
                    window.location.reload();
                }, 2000);

            }catch(error){
                console.error('Import error:', error);
                showAlert('error', 'File reading error or invalid format');
            }finally{
                setLoading(false);
            }
        };
        input.click();
    };

    const showAlert = (type: 'success' | 'error', message: string) => {
        const event = new CustomEvent('showAlert', {
            detail: {type, message}
        });
        window.dispatchEvent(event);
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');

        if (!oldPassword || !newPassword || !confirmPassword) {
            setPasswordError('All fields are required');
            return;
        }

        if (newPassword.length < 6) {
            setPasswordError('New password must be at least 6 characters long');
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError('New passwords do not match');
            return;
        }

        setChangingPassword(true);
        try {
            await authAPI.changePassword({ oldPassword, newPassword });
            setPasswordSuccess('Password changed successfully');
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
            showAlert('success', 'Password changed successfully');
        } catch (error: any) {
            setPasswordError(error.response?.data?.message || 'Failed to change password');
            showAlert('error', error.response?.data?.message || 'Failed to change password');
        } finally {
            setChangingPassword(false);
        }
    };

    useEffect(() => {
        if (!hasLoggedPageView.current) {
            loggingService.logPageView('/settings', null, 'Settings');
            hasLoggedPageView.current = true;
        }
    }, []);

    useEffect(() => {
        if(activeTab === 'logs'){
            const loadLogs = async() => {
                setLogsLoading(true);
                try{
                    const response = await api.get('/activity-logs', { params: { limit: 100 } });
                    // Filter: only show login, logout, and page_view (module access) logs
                    // Exclude logs with log_api_type (module-specific logs)
                    const filteredLogs = response.data.data.filter((log: ActivityLog) => 
                        (log.action === 'login' || log.action === 'logout' || log.action === 'page_view')
                    );
                    setLogs(filteredLogs);
                }catch(error){
                    console.error('Error loading logs:', error);
                    showAlert('error', 'Error occurred while loading logs');
                }finally{
                    setLogsLoading(false);
                }
            };
            loadLogs();
        }
    }, [activeTab]);

    const getActionIcon = (action: string) => {
        switch(action){
            case 'login': return '🔐';
            case 'logout': return '🚪';
            case 'api_key_copy': return '📋';
            case 'password_copy': return '📋';
            case 'api_key_create': return '➕';
            case 'password_create': return '➕';
            case 'api_key_update': return '✏️';
            case 'password_update': return '✏️';
            case 'api_key_delete': return '🗑️';
            case 'password_delete': return '🗑️';
            case 'search': return '🔍';
            case 'page_view': return '👁️';
            case 'export': return '📤';
            case 'import': return '📥';
            case 'settings_change': return '⚙️';
            default: return '📝';
        }
    };

    const getActionColor = (action: string) => {
        if(action.includes('copy')) return 'text-blue-600';
        if(action.includes('create')) return 'text-green-600';
        if(action.includes('update')) return 'text-yellow-600';
        if(action.includes('delete')) return 'text-red-600';
        if(action.includes('login') || action.includes('logout')) return 'text-purple-600';
        return 'text-gray-600';
    };

    return (
        <div className="max-w-3xl mx-auto px-2 md:px-0 space-y-4 pb-20 font-mono">
            {/* Header */}
            <div className={`p-4 border ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
                <h1 className={`text-sm font-bold tracking-widest uppercase ${isDark ? 'text-neutral-200' : 'text-neutral-800'}`}>
                    // SYS: USER_SETTINGS
                </h1>
                <p className={`text-[10px] mt-0.5 uppercase tracking-wider ${isDark ? 'text-emerald-500/70' : 'text-emerald-600/70'}`}>
                    MANAGE_ACCOUNT_PREFERENCES
                </p>
            </div>

            {/* Tabs */}
            <div className={`flex items-center gap-1 border-b ${isDark ? 'border-[#222]' : 'border-neutral-200'} overflow-x-auto`}>
                {(['general', 'password', 'data', 'logs'] as const).map((tab) => {
                    const labels: Record<string, string> = {
                        general: 'GENERAL',
                        password: 'CHANGE_PWD',
                        data: 'DATA_MGMT',
                        logs: 'ACTIVITY_LOGS'
                    };
                    return (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-2 text-[10px] tracking-widest uppercase border-b-2 transition-colors flex-shrink-0 ${
                                activeTab === tab
                                    ? isDark ? 'border-emerald-500 text-emerald-400' : 'border-emerald-600 text-emerald-700'
                                    : isDark ? 'border-transparent text-neutral-500 hover:text-neutral-300' : 'border-transparent text-neutral-500 hover:text-neutral-700'
                            }`}
                        >
                            {labels[tab]}
                        </button>
                    );
                })}
            </div>

            {activeTab === 'general' && (
                <div className="space-y-4">
                    {/* User Info */}
                    <div className={`p-4 border ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
                        <h2 className={`text-[10px] tracking-widest uppercase mb-4 pb-2 border-b ${isDark ? 'text-emerald-500 border-[#333]' : 'text-emerald-600 border-neutral-200'}`}>
                            USER_IDENTITY
                        </h2>
                        <div className="space-y-3">
                            <div className={`flex items-center justify-between text-[11px] py-2 border-b ${isDark ? 'border-[#222] text-neutral-300' : 'border-neutral-100 text-neutral-700'}`}>
                                <span className={`uppercase tracking-wider ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>USERNAME</span>
                                <span className={`font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{user?.username}</span>
                            </div>
                            <div className={`flex items-center justify-between text-[11px] py-2 ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
                                <span className={`uppercase tracking-wider ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>USER_ID</span>
                                <span className={isDark ? 'text-neutral-300' : 'text-neutral-700'}>{user?.id}</span>
                            </div>
                        </div>
                    </div>

                    {/* Logout */}
                    <div className={`p-4 border ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
                        <h2 className={`text-[10px] tracking-widest uppercase mb-4 pb-2 border-b ${isDark ? 'text-emerald-500 border-[#333]' : 'text-emerald-600 border-neutral-200'}`}>
                            TERMINATE_SESSION
                        </h2>
                        <p className={`text-[10px] mb-4 uppercase tracking-wider ${isDark ? 'text-neutral-500' : 'text-neutral-500'}`}>
                            // CLICK TO TERMINATE CURRENT AUTH SESSION.
                        </p>
                        <button
                            onClick={() => {
                                logout();
                                navigate('/login');
                            }}
                            className={`flex items-center space-x-2 px-6 py-2 text-[10px] uppercase tracking-widest border transition-colors ${isDark ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                        >
                            <LogOut className="w-3.5 h-3.5" />
                            <span>LOGOUT</span>
                        </button>
                    </div>
                </div>
            )}

            {activeTab === 'password' && (
                <div className={`p-4 border ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
                    <h2 className={`text-[10px] tracking-widest uppercase mb-4 pb-2 border-b ${isDark ? 'text-emerald-500 border-[#333]' : 'text-emerald-600 border-neutral-200'}`}>
                        CHANGE_AUTH_KEY
                    </h2>
                    <form onSubmit={handleChangePassword} className="space-y-4 text-[10px] uppercase tracking-wider">
                        <div>
                            <label className={`block mb-1.5 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>OLD_PASSWORD</label>
                            <input
                                type="password"
                                value={oldPassword}
                                onChange={(e) => setOldPassword(e.target.value)}
                                className={`w-full px-4 py-2 border outline-none transition-colors ${isDark ? 'bg-[#0a0a0a] border-[#333] text-neutral-200 focus:border-emerald-500/50' : 'bg-neutral-50 border-neutral-300 text-neutral-800 focus:border-emerald-500/50'}`}
                                required
                            />
                        </div>
                        <div>
                            <label className={`block mb-1.5 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>NEW_PASSWORD</label>
                            <input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className={`w-full px-4 py-2 border outline-none transition-colors ${isDark ? 'bg-[#0a0a0a] border-[#333] text-neutral-200 focus:border-emerald-500/50' : 'bg-neutral-50 border-neutral-300 text-neutral-800 focus:border-emerald-500/50'}`}
                                required
                                minLength={6}
                            />
                        </div>
                        <div>
                            <label className={`block mb-1.5 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>CONFIRM_NEW_PASSWORD</label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className={`w-full px-4 py-2 border outline-none transition-colors ${isDark ? 'bg-[#0a0a0a] border-[#333] text-neutral-200 focus:border-emerald-500/50' : 'bg-neutral-50 border-neutral-300 text-neutral-800 focus:border-emerald-500/50'}`}
                                required
                                minLength={6}
                            />
                        </div>
                        {passwordError && (
                            <div className={`p-3 border ${isDark ? 'bg-red-900/10 border-red-500/30 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>
                                <p className="text-[10px]">{passwordError}</p>
                            </div>
                        )}
                        {passwordSuccess && (
                            <div className={`p-3 border ${isDark ? 'bg-emerald-900/10 border-emerald-500/30 text-emerald-400' : 'bg-emerald-50 border-emerald-200 text-emerald-600'}`}>
                                <p className="text-[10px]">{passwordSuccess}</p>
                            </div>
                        )}
                        <div className="flex justify-end pt-2">
                            <button
                                type="submit"
                                disabled={changingPassword}
                                className={`px-6 py-2 text-[10px] uppercase tracking-widest border flex items-center space-x-2 ${
                                    changingPassword
                                        ? isDark ? 'border-emerald-500/20 text-emerald-500/40 cursor-not-allowed' : 'border-emerald-200 text-emerald-400 cursor-not-allowed'
                                        : isDark ? 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                                }`}
                            >
                                {changingPassword ? (
                                    <><div className="w-1 h-2 bg-current animate-pulse mr-1" />CHANGING...</>
                                ) : (
                                    <>APPLY_NEW_PASSWORD</>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {activeTab === 'data' && (
                <div className={`p-4 border ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
                    <h2 className={`text-[10px] tracking-widest uppercase mb-4 pb-2 border-b ${isDark ? 'text-emerald-500 border-[#333]' : 'text-emerald-600 border-neutral-200'}`}>
                        DATA_MANAGEMENT
                    </h2>
                    <div className="space-y-3">
                        <button
                            onClick={handleExport}
                            disabled={loading}
                            className={`w-full flex items-center justify-center space-x-2 px-4 py-3 text-[10px] uppercase tracking-widest border transition-colors ${isDark ? 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 bg-[#0a0a0a]' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50 bg-neutral-50'}`}
                        >
                            <Download className="w-3.5 h-3.5" />
                            <span>{loading ? 'EXPORTING...' : 'EXPORT_DATA'}</span>
                        </button>
                        <button
                            onClick={handleImport}
                            disabled={loading}
                            className={`w-full flex items-center justify-center space-x-2 px-4 py-3 text-[10px] uppercase tracking-widest border transition-colors ${isDark ? 'border-[#333] text-neutral-400 hover:bg-[#222] bg-[#0a0a0a]' : 'border-neutral-300 text-neutral-600 hover:bg-neutral-100 bg-neutral-50'}`}
                        >
                            <Upload className="w-3.5 h-3.5" />
                            <span>{loading ? 'IMPORTING...' : 'IMPORT_DATA'}</span>
                        </button>
                    </div>
                </div>
            )}

            {activeTab === 'logs' && (
                <div className={`border ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-neutral-200'}`}>
                    <div className={`px-4 py-3 border-b ${isDark ? 'border-[#333]' : 'border-neutral-200'}`}>
                        <h2 className={`text-[10px] tracking-widest uppercase ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
                            ACTIVITY_HISTORY
                        </h2>
                    </div>
                    {logsLoading ? (
                        <div className={`flex flex-col items-center justify-center h-32 font-mono text-xs ${isDark ? 'text-emerald-500' : 'text-emerald-600'}`}>
                            <div className="flex items-center space-x-2">
                                <div className="w-1 h-3 bg-current animate-pulse" />
                                <span>[ FETCHING_LOGS... ]</span>
                            </div>
                        </div>
                    ) : logs.length === 0 ? (
                        <div className={`p-8 text-center text-[10px] uppercase tracking-widest ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>
                            [ NO_LOGS_FOUND ]
                        </div>
                    ) : (
                        <div className="divide-y divide-[#222]">
                            {logs.map((log) => (
                                <div key={log.id} className={`flex items-start gap-3 px-4 py-2.5 transition-colors ${isDark ? 'hover:bg-[#1a1a1a]' : 'hover:bg-neutral-50'}`}>
                                    <span className="text-sm flex-shrink-0">{getActionIcon(log.action)}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                            <p className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
                                                {log.description}
                                                {log.metadata?.module && (
                                                    <span className={`ml-1 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>({log.metadata.module})</span>
                                                )}
                                            </p>
                                            <span className={`text-[9px] flex-shrink-0 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
                                                {new Date(log.created_at).toLocaleString('tr-TR')}
                                            </span>
                                        </div>
                                        {log.ip_address && (
                                            <p className={`text-[8px] mt-0.5 ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>{log.ip_address}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Settings;
